import React, { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import * as PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ReactComponent as NewChatIcon } from '@assets/images/chat/new-chat.svg';

import Loading from '$app-web/components/loading';
import LoadingIndicatorBar from '$app-web/ui-kit/components/loading-indicator-bar';

import { MATRIX_TIMELINE_PAGINATE_DIRECTIONS, MATRIX_TIMELINE_PAGINATE_EVENTS_LIMIT, TIMELINE_STATUS } from '../../constants';
import { useChatUserData, useCurrentStudyRooms, useMethods } from '../../state/chat-client.state';
import GoToBottomButton from './go-to-bottom-button';
import MessagesList from './messages-list';
import classes from './styles.module.scss';

const Timeline = (props) => {
    const { status, roomId, canLoadBackward, loadingBackward, events, otherMembersLastReadMarker, direct, liveEvent } = props;

    const { t } = useTranslation();

    const methods = useMethods();
    const chatUserData = useChatUserData();
    const { unreadMessagesCount } = useCurrentStudyRooms()[roomId];

    const [showGoToBottomButton, setShowGoToBottomButton] = useState(false);

    const scrollableWrapperRef = useRef(null);
    const scrollTicking = useRef(false);
    const scrollData = useRef({ top: -1, height: -1, visibleHeight: -1 });
    const canLoadBackwardRef = useRef(canLoadBackward);
    const scrollOffsetRef = useRef(null);
    const firstEventRef = useRef(null);
    const lastEventRef = useRef(null);
    const lastVisibleEventIndexRef = useRef(events.length - 1);
    const invisibleBelowEventsHeightRef = useRef(0);

    // TODO: If we scroll quickly to the bottom, the go-to-bottom button won't hide :D
    const onScroll = useCallback(
        (event) => {
            const {
                scrollHeight,
                scrollTop,
                clientHeight,
                children: [{ children: eventElements }],
            } = scrollableWrapperRef.current;
            const scrollBottom = scrollHeight - clientHeight - scrollTop;

            !unreadMessagesCount && setShowGoToBottomButton(true);
            if (
                invisibleBelowEventsHeightRef.current + eventElements[lastVisibleEventIndexRef.current]?.clientHeight <
                scrollBottom
            ) {
                invisibleBelowEventsHeightRef.current =
                    eventElements[lastVisibleEventIndexRef.current].clientHeight + invisibleBelowEventsHeightRef.current;
                lastVisibleEventIndexRef.current -= 1;
            } else if (
                lastVisibleEventIndexRef.current < events.length - 1 &&
                invisibleBelowEventsHeightRef.current > scrollBottom
            ) {
                lastVisibleEventIndexRef.current += 1;
                invisibleBelowEventsHeightRef.current -= eventElements[lastVisibleEventIndexRef.current].clientHeight;
                methods.updateRoomReadRecipient(roomId, lastVisibleEventIndexRef.current);
                setShowGoToBottomButton(true);
            } else if (lastVisibleEventIndexRef.current === events.length - 1) {
                setShowGoToBottomButton(false);
                methods.updateRoomReadRecipient(roomId, lastVisibleEventIndexRef.current);
            }

            scrollOffsetRef.current = event.target.scrollHeight - event.target.scrollTop;
            if (!scrollTicking.current) {
                window.requestAnimationFrame(() => {
                    scrollData.current = {
                        top: event.target.scrollTop,
                        height: event.target.scrollHeight,
                        visibleHeight: event.target.clientHeight,
                    };
                    if (scrollData.current.top < 10 && canLoadBackwardRef.current) {
                        scrollOffsetRef.current = event.target.scrollHeight;
                        methods.loadTimeline(roomId, MATRIX_TIMELINE_PAGINATE_DIRECTIONS.BACKWARDS);
                    }
                    scrollTicking.current = false;
                });

                scrollTicking.current = true;
            }
        },
        [methods, roomId, events.length, unreadMessagesCount]
    );

    const onGoToBottomClick = () => {
        scrollableWrapperRef.current.scrollTop = scrollableWrapperRef.current.scrollHeight;
        setShowGoToBottomButton(false);
        lastVisibleEventIndexRef.current = events.length - 1;
        invisibleBelowEventsHeightRef.current = 0;
    };

    const scrollableWrapperCallbackRef = useCallback(
        (node) => {
            if (node !== null) {
                scrollableWrapperRef.current = node;
                scrollableWrapperRef.current.addEventListener('scroll', onScroll, { passive: true });
                scrollableWrapperRef.current.scrollTop = scrollableWrapperRef.current.scrollHeight - scrollOffsetRef.current;

                if (scrollOffsetRef.current < (node.lastChild?.lastChild?.clientHeight || 0) + node.clientHeight) {
                    lastVisibleEventIndexRef.current = events.length - 1;
                    setShowGoToBottomButton(false);
                    invisibleBelowEventsHeightRef.current = 0;
                }
            }
        },
        [onScroll, events.length]
    );

    useEffect(() => {
        if (status === TIMELINE_STATUS.UNKNOWN && events.length <= MATRIX_TIMELINE_PAGINATE_EVENTS_LIMIT) {
            methods.loadTimeline(roomId);
        }

        if (events.length > 0) {
            methods.updateRoomReadRecipient(roomId, lastVisibleEventIndexRef.current);
        }
    }, [methods, roomId, status, events]);

    useEffect(() => {
        const scrollableWrapperRefVariable = scrollableWrapperRef.current;
        return () => {
            if (scrollableWrapperRefVariable !== null) {
                scrollableWrapperRefVariable.removeEventListener('scroll', onScroll);
            }
        };
    }, [onScroll]);

    useEffect(() => {
        canLoadBackwardRef.current = canLoadBackward;
    }, [canLoadBackward]);

    useEffect(() => {
        const currentLastEvent = events[events.length - 1];
        if (liveEvent) {
            if (chatUserData.userId === currentLastEvent?.getSender()) {
                scrollOffsetRef.current = 0;
            } else if (
                currentLastEvent?.event?.event_id !== lastEventRef.current?.event?.event_id &&
                lastVisibleEventIndexRef.current !== events.length - 1
            ) {
                scrollOffsetRef.current += scrollableWrapperRef.current.children[0].children[events.length - 1].clientHeight;
                lastVisibleEventIndexRef.current += 1;
                setShowGoToBottomButton(true);
            }
        }
        lastEventRef.current = currentLastEvent;
    }, [events, chatUserData.userId, liveEvent]);

    useEffect(() => {
        if (firstEventRef.current === null) {
            [firstEventRef.current] = events;
        } else if (events[0]?.event.event_id !== firstEventRef.current?.event.event_id) {
            lastVisibleEventIndexRef.current += events.findIndex(
                ({ event }) => event.event_id === firstEventRef.current?.event.event_id
            );
            [firstEventRef.current] = events;
        }
    }, [events]);

    return (
        <div className={clsx('item size-fill', classes.root)}>
            {status !== TIMELINE_STATUS.UNKNOWN && loadingBackward && (
                <div className={classes.loadingWrapper}>
                    <LoadingIndicatorBar />
                </div>
            )}
            <Loading
                variant="parent-overlay"
                loading={status === TIMELINE_STATUS.UNKNOWN}
                error={status === TIMELINE_STATUS.FAILED}
            />
            <div className="scrollable" ref={scrollableWrapperCallbackRef}>
                {events.length > 0 ? (
                    <>
                        <MessagesList events={events} otherMembersLastReadMarker={otherMembersLastReadMarker} direct={direct} />
                        {showGoToBottomButton && (
                            <GoToBottomButton
                                badgeValue={unreadMessagesCount}
                                className={classes.goToBottomButton}
                                onClick={onGoToBottomClick}
                            />
                        )}
                    </>
                ) : (
                    status !== TIMELINE_STATUS.UNKNOWN && (
                        <div className={clsx('flex column justify-center items-center', classes.emptyRoomContent)}>
                            <NewChatIcon />
                            <span className="typo-old weight-normal ">{t('no_messages_here_yet')}</span>
                            <span className="typo-old body1">{t('chat_start_text')}</span>
                        </div>
                    )
                )}
            </div>
        </div>
    );
};

Timeline.defaultProps = {
    otherMembersLastReadMarker: undefined,
    liveEvent: false,
};

Timeline.propTypes = {
    status: PropTypes.string.isRequired,
    events: PropTypes.arrayOf(PropTypes.object).isRequired,
    canLoadBackward: PropTypes.bool.isRequired,
    loadingBackward: PropTypes.bool.isRequired,
    roomId: PropTypes.string.isRequired,
    otherMembersLastReadMarker: PropTypes.oneOfType([PropTypes.object]),
    direct: PropTypes.bool.isRequired,
    liveEvent: PropTypes.bool,
};

export default Timeline;
